import { z } from 'zod';
import { fileMetadataSchema } from '../common/metadata';

/**
 * Zod schemas for file-related entities
 * These provide runtime validation and type inference
 */

function hasNonEmptyString(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

/** Shared field shape for FileRecord (read and write). */
const fileRecordFieldsSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'File name is required'),
  originalName: z.string().min(1, 'Original file name is required').optional(),
  type: z.string().min(1, 'File type is required'),
  size: z.union([z.number().nonnegative(), z.string()]).optional(), // Allow number or string (for TenantDocument)
  content: z.string().optional(), // base64 encoded - optional for portal documents
  uploadedBy: z.string().optional(),
  uploadedAt: z.string().optional(),
  date: z.string().optional(), // TenantDocument uses 'date' instead of 'uploadedAt'
  tenantId: z.string().optional(), // TenantDocument-specific field
  entityType: z.enum(['Property', 'Tenant', 'Lease', 'Ticket', 'Bill', 'Document', 'Other'] as const).optional(),
  entityId: z.string().optional(),
  /** Tier-C strict anchors (at most one; XOR with polymorphic entityId/entityType for new writes). */
  leaseId: z.string().optional(),
  paymentId: z.string().optional(),
  receivableId: z.string().optional(),
  propertyId: z.string().optional(),
  documentCategory: z.string().optional(),
  isPublic: z.boolean().optional(),
  accessToken: z.string().optional(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  folder: z.string().optional(),
  metadata: fileMetadataSchema.optional(),
  downloads: z.number().int().nonnegative().optional(),
  lastDownloadedAt: z.string().optional(),
  lastDownloadedBy: z.string().optional(),
  url: z.string().optional(), // Component-used property
  data: z.string().optional(), // Component-used property
  category: z.string().optional(), // TenantDocument-specific field
  sourceApplicationId: z.string().optional(), // TenantDocument-specific field
}).catchall(z.unknown());

/**
 * List/read responses: legacy rows may still violate Phase 4 CHECKs until backfilled.
 */
export const fileRecordResponseSchema = fileRecordFieldsSchema;

/**
 * Create/update payloads: align with Postgres CHECKs (payment XOR receivable, single strict anchor, Trap 3).
 */
export const fileRecordSchema = fileRecordFieldsSchema.superRefine((data, ctx) => {
    const pay = hasNonEmptyString(data.paymentId);
    const rec = hasNonEmptyString(data.receivableId);
    if (pay && rec) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cannot set both paymentId and receivableId',
        path: ['paymentId'],
      });
    }

    const lease = hasNonEmptyString(data.leaseId);
    const prop = hasNonEmptyString(data.propertyId);
    const strictCount = [lease, pay, rec, prop].filter(Boolean).length;
    if (strictCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At most one of leaseId, paymentId, receivableId, propertyId may be set',
        path: ['leaseId'],
      });
    }

    if (strictCount >= 1) {
      if (hasNonEmptyString(data.entityId) || hasNonEmptyString(data.entityType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'entityId and entityType must be omitted when a strict anchor (leaseId, paymentId, receivableId, propertyId) is set (Trap 3)',
          path: ['entityType'],
        });
      }
    }
  });

export type FileRecord = z.infer<typeof fileRecordResponseSchema>;

/** Tenant portal document row (`TenantDocument` model). */
export const tenantDocumentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  type: z.string(),
  date: z.string(),
  size: z.union([z.string(), z.number()]),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  sourceApplicationId: z.string().optional(),
  url: z.string().optional(),
});

export type TenantDocumentDto = z.infer<typeof tenantDocumentSchema>;
